import { useMarketAccountsContext } from '@talos/kyoko';
import { useMemo } from 'react';
import { OrgConfigurationKey, useOrgConfiguration } from '../../../providers';

/**
 * Not all Market Accounts can have their Balances updated. These are set as an OrgConfig. This hook
 * reads from the OrgConfig and returns details to be used by the rest of the app in updating balances flows.
 */
export const useUpdateBalancesAccounts = () => {
  const { getJSONArrayConfig } = useOrgConfiguration();
  const { marketAccountsList, marketAccountsByMarket } = useMarketAccountsContext();

  const configuredAccounts: Set<string> | undefined = useMemo(() => {
    const arr = getJSONArrayConfig<string>(OrgConfigurationKey.BalancePublishMarketAccountNames);
    return arr ? new Set(arr) : undefined;
  }, [getJSONArrayConfig]);

  const configuredMarkets: Set<string> | undefined = useMemo(() => {
    const arr = getJSONArrayConfig<string>(OrgConfigurationKey.BalancePublishMarketNames);
    return arr ? new Set(arr) : undefined;
  }, [getJSONArrayConfig]);

  const result: { accounts?: string[]; accountsSet?: Set<string> } = useMemo(() => {
    if (!configuredAccounts && !configuredMarkets) {
      return {};
    }

    const combinedConfiguredAccounts = configuredAccounts ? new Set(configuredAccounts) : new Set();

    // Grab all MarketAccounts for the configured markets and add them into the configured accounts set
    configuredMarkets?.forEach(market => {
      marketAccountsByMarket.get(market)?.forEach(mktAcc => {
        combinedConfiguredAccounts.add(mktAcc.Name);
      });
    });

    // The combinedConfiguredAccounts list might have some market accounts that don't actually exist in the system. Exclude these here.
    const accounts = marketAccountsList.filter(acc => combinedConfiguredAccounts.has(acc.Name)).map(acc => acc.Name);

    return { accounts, accountsSet: new Set(accounts) };
  }, [configuredAccounts, configuredMarkets, marketAccountsList, marketAccountsByMarket]);

  return result;
};
